import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  Grid,
  makeStyles,
  Backdrop,
  CircularProgress,
  Typography,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(3),
  },

  card: {
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "none",
    overflow: "hidden",
    backgroundColor: "#fff",
  },

  cardHeader: {
    padding: theme.spacing(2.25, 2.5),
    backgroundColor: "#fafafa",
  },

  cardTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#1f2937",
  },

  content: {
    padding: theme.spacing(2.5),
  },

  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },

  imagePreviewBox: {
    minHeight: 420,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
    backgroundColor: "#fafafa",
  },

  imagePreview: {
    width: "100%",
    maxHeight: 390,
    objectFit: "contain",
  },

  uploadSection: {
    padding: theme.spacing(2),
    backgroundColor: "#fff",
  },

  uploadTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 4,
  },

  uploadHint: {
    fontSize: 11,
    color: "#6b7280",
    lineHeight: 1.5,
    marginBottom: theme.spacing(1.5),
  },

  actionSection: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(2),
  },

  actionButton: {
    minHeight: 42,
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    paddingLeft: 18,
    paddingRight: 18,
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const escapeHtml = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const Views = () => {
  const classes = useStyles();

  const [artist, setArtist] = useState("");
  const [title, setTitle] = useState("");
  const [year, setYear] = useState("");
  const [medium, setMedium] = useState("");
  const [size, setSize] = useState("");

  const [imageData, setImageData] = useState("");
  const [imagePicture, setImagePicture] = useState(null);

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image") {
      const file = files && files[0];

      if (!file) {
        setImagePicture(null);
        setImageData("");
        return;
      }

      setImagePicture(file);

      const reader = new FileReader();

      reader.onload = (event) => {
        setImageData(event.target.result);
      };

      reader.readAsDataURL(file);
      return;
    }

    switch (name) {
      case "artist":
        setArtist(value);
        break;

      case "title":
        setTitle(value);
        break;

      case "year":
        setYear(value);
        break;

      case "medium":
        setMedium(value);
        break;

      case "size":
        setSize(value);
        break;

      default:
        break;
    }
  };

  const handlePrintPreview = () => {
    if (!imageData) {
      alert("Please upload an image first!");
      return;
    }

    setLoading(true);

    const printWindow = window.open(
      "",
      "_blank",
      "width=1200,height=900"
    );

    if (!printWindow) {
      setLoading(false);
      alert("Please allow pop-ups for this website.");
      return;
    }

    const artworkName =
      artist || title || "Certification";

    printWindow.document.open();

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />

          <title>
            ${escapeHtml(artworkName)}
          </title>

          <style>
            @page {
              size: Letter portrait;
              margin: 0;
            }

            * {
              box-sizing: border-box;
            }

            html,
            body {
              margin: 0;
              padding: 0;
              background: #e5e7eb;
              color: #000;
              font-family: Arial, Helvetica, sans-serif;
            }

            body {
              padding-top: 60px;
              padding-bottom: 30px;
            }

            /* =======================================
               TOP BAR
            ======================================= */

            .topbar {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;

              height: 60px;

              display: flex;
              align-items: center;
              justify-content: space-between;

              padding: 0 20px;

              background: #ffffff;

              border-bottom: 1px solid #d1d5db;

              box-shadow:
                0 2px 8px rgba(0, 0, 0, 0.08);

              z-index: 9999;
            }

            .preview-title {
              font-size: 14px;
              font-weight: 600;
              color: #374151;
            }

            .print-button {
              border: none;

              background: #1976d2;
              color: white;

              padding: 10px 18px;

              border-radius: 7px;

              font-size: 14px;
              font-weight: 600;

              cursor: pointer;
            }

            .print-button:hover {
              background: #1565c0;
            }

            /* =======================================
               PAPER
            ======================================= */

            .paper {
              width: 215.9mm;
              height: 279.4mm;

              margin: 20px auto;

              padding: 12mm 14mm;

              background: #ffffff;

              position: relative;

              box-shadow:
                0 5px 25px rgba(0, 0, 0, 0.15);

              overflow: hidden;

              page-break-after: always;
              break-after: page;
            }

            /* =======================================
               HEADER
            ======================================= */

            .page-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;

              padding-bottom: 10px;

              border-bottom: 1px solid #000;
            }

            .logo {
              width: 150px;
              height: auto;
              display: block;
            }

            .contact {
              text-align: right;

              font-size: 8px;

              line-height: 1.5;

              color: #333;
            }

            /* =======================================
               DOCUMENT TITLE
            ======================================= */

            .document-title {
              text-align: center;

              font-size: 20px;

              font-weight: 700;

              letter-spacing: 0.5px;

              margin-top: 18px;
              margin-bottom: 8px;

              text-transform: uppercase;
            }

            .document-subtitle {
              text-align: center;

              max-width: 155mm;

              margin: 0 auto;

              font-size: 11px;

              line-height: 1.5;

              color: #333;
            }

            /* =======================================
               ARTWORK
            ======================================= */

            .artwork-area {
              width: 100%;

              height: 92mm;

              margin-top: 12mm;

              display: flex;

              align-items: center;
              justify-content: center;

              padding: 5mm;

              border: 1px solid #e5e7eb;

              background: #fafafa;
            }

            .artwork {
              display: block;

              max-width: 100%;
              max-height: 82mm;

              width: auto;
              height: auto;

              object-fit: contain;
            }

            /* =======================================
               ARTWORK DETAILS
            ======================================= */

            .details {
              text-align: center;

              margin-top: 10mm;
            }

            .artist {
              font-size: 19px;

              font-weight: 700;

              line-height: 1.25;

              margin-bottom: 3mm;

              word-break: break-word;
            }

            .title {
              font-size: 14px;

              font-style: italic;

              line-height: 1.35;

              color: #222;

              word-break: break-word;
            }

            .year {
              font-size: 14px;

              line-height: 1.35;

              color: #222;
            }

            .medium,
            .size {
              font-size: 12px;

              line-height: 1.45;

              color: #333;
            }

            /* =======================================
               SIGNATURE
            ======================================= */

            .signature-section {
              position: absolute;

              right: 18mm;

              bottom: 30mm;

              width: 55mm;

              text-align: center;
            }

            .signature-line {
              border-top: 1px solid #000;

              padding-top: 6px;
            }

            .signature-label {
              font-size: 9px;

              font-weight: 700;

              text-transform: uppercase;

              letter-spacing: 0.5px;
            }

            /* =======================================
               FOOTER
            ======================================= */

            .footer {
              position: absolute;

              left: 14mm;
              right: 14mm;

              bottom: 10mm;

              text-align: center;

              font-size: 7.5px;

              color: #666;

              line-height: 1.5;
            }

            /* =======================================
               EDITABLE
            ======================================= */

            .editable {
              outline: none;

              border-radius: 2px;
            }

            .editable:hover {
              background: #fffde7;
            }

            .editable:focus {
              background: #fff8cc;

              box-shadow:
                inset 0 0 0 1px #d6b656;
            }

            /* =======================================
               PRINT
            ======================================= */

            @media print {
              html,
              body {
                margin: 0 !important;
                padding: 0 !important;

                background: #ffffff !important;
              }

              body {
                padding: 0 !important;
              }

              .topbar {
                display: none !important;
              }

              .paper {
                width: 215.9mm !important;
                height: 279.4mm !important;

                margin: 0 !important;

                padding: 12mm 14mm !important;

                box-shadow: none !important;

                overflow: hidden !important;

                page-break-after: always !important;
                break-after: page !important;
              }

              .paper:last-child {
                page-break-after: auto !important;
                break-after: auto !important;
              }

              .editable:hover,
              .editable:focus {
                background: transparent !important;

                box-shadow: none !important;
              }
            }
          </style>
        </head>

        <body>

          <div class="topbar">

            <div class="preview-title">
              Certificate Preview
            </div>

            <button
              class="print-button"
              onclick="window.print()"
            >
              🖨 Print
            </button>

          </div>

          <div class="paper">

            <!-- HEADER -->

            <div class="page-header">

              <div>
                <img
                  src="/static/logo140.png"
                  class="logo"
                  alt="Leon Gallery"
                />
              </div>

              <div class="contact">

                <div>
                  <b>www.leon-gallery.com</b>
                </div>

                <div>
                  leongallerymakati@gmail.com
                </div>

                <div>
                  info@leon-gallery.com
                </div>

              </div>

            </div>

            <!-- TITLE -->

            <div
              class="document-title editable"
              contenteditable="true"
              spellcheck="false"
            >
              CERTIFICATION
            </div>

            <div
              class="document-subtitle editable"
              contenteditable="true"
              spellcheck="false"
            >
              This is to certify that the piece with picture and details below was purchased from Leon Gallery.
            </div>

            <!-- ARTWORK -->

            <div class="artwork-area">

              <img
                src="${escapeHtml(imageData)}"
                class="artwork"
                alt="Artwork"
              />

            </div>

            <!-- DETAILS -->

            <div class="details">

              ${
                artist
                  ? `
                    <div
                      class="artist editable"
                      contenteditable="true"
                      spellcheck="false"
                    >
                      ${escapeHtml(artist)}
                    </div>
                  `
                  : ""
              }

              ${
                title
                  ? `
                    <span
                      class="title editable"
                      contenteditable="true"
                      spellcheck="false"
                    >
                      ${escapeHtml(title)}
                    </span>
                  `
                  : ""
              }

              ${
                year
                  ? `
                    <span
                      class="year editable"
                      contenteditable="true"
                      spellcheck="false"
                    >
                      , ${escapeHtml(year)}
                    </span>
                  `
                  : ""
              }

              ${
                title || year
                  ? `<br />`
                  : ""
              }

              ${
                medium
                  ? `
                    <div
                      class="medium editable"
                      contenteditable="true"
                      spellcheck="false"
                    >
                      ${escapeHtml(medium)}
                    </div>
                  `
                  : ""
              }

              ${
                size
                  ? `
                    <div
                      class="size editable"
                      contenteditable="true"
                      spellcheck="false"
                    >
                      ${escapeHtml(size)}
                    </div>
                  `
                  : ""
              }

            </div>

            <!-- SIGNATURE -->

            <div class="signature-section">

              <div class="signature-line">

                <div
                  class="signature-label editable"
                  contenteditable="true"
                  spellcheck="false"
                >
                  Authorized Signature
                </div>

              </div>

            </div>

            <!-- FOOTER -->

            <div class="footer">

              <div>
                G/F Eurovilla 1, Rufino Corner Legazpi Streets,
                Legazpi Village, Makati City, Philippines
              </div>

              <div>
                Tel no. (632) 856-2781 | www.leon-gallery.com
              </div>

            </div>

          </div>

        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      setLoading(false);
    }, 400);
  };

  const previewImage = imagePicture
    ? URL.createObjectURL(imagePicture)
    : "/static/blank.jpeg";

  return (
    <div className={classes.root}>
      <Grid container spacing={3}>

        {/* =========================================
            IMAGE PREVIEW
        ========================================= */}

        <Grid item lg={4} md={5} xs={12}>
          <Card className={classes.card}>

            <CardHeader
              className={classes.cardHeader}
              title={
                <span className={classes.cardTitle}>
                  Artwork Image
                </span>
              }
            />

            <Divider />

            <CardContent className={classes.imagePreviewBox}>
              <img
                className={classes.imagePreview}
                src={previewImage}
                alt="Artwork Preview"
              />
            </CardContent>

            <Divider />

            <CardContent className={classes.uploadSection}>
              <Typography className={classes.uploadTitle}>
                Upload Artwork
              </Typography>

              <Typography className={classes.uploadHint}>
                Select the image you want to use for the certificate.
              </Typography>

              <TextField
                fullWidth
                type="file"
                name="image"
                onChange={handleChange}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                className={classes.field}
                inputProps={{
                  accept: "image/*",
                }}
              />
            </CardContent>

          </Card>
        </Grid>

        {/* =========================================
            CERTIFICATE FORM
        ========================================= */}

        <Grid item lg={8} md={7} xs={12}>
          <Card className={classes.card}>

            <CardHeader
              className={classes.cardHeader}
              title={
                <span className={classes.cardTitle}>
                  Create Certification
                </span>
              }
            />

            <Divider />

            <CardContent className={classes.content}>

              <Grid container spacing={2}>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="artist"
                    label="Artist Name"
                    value={artist}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="e.g. Juan Luna"
                    className={classes.field}
                  />
                </Grid>

                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    name="title"
                    label="Title"
                    value={title}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="e.g. Spoliarium"
                    className={classes.field}
                  />
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    name="year"
                    label="Year"
                    value={year}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="e.g. 1884"
                    className={classes.field}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="medium"
                    label="Medium"
                    value={medium}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="e.g. Oil on Canvas"
                    className={classes.field}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="size"
                    label="Size"
                    value={size}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="e.g. 422 cm x 767 cm"
                    className={classes.field}
                  />
                </Grid>

              </Grid>

            </CardContent>

            <Divider />

            <Box className={classes.actionSection}>

              <Button
                color="primary"
                variant="contained"
                className={classes.actionButton}
                onClick={handlePrintPreview}
                disabled={!imageData}
              >
                Preview & Print
              </Button>

            </Box>

          </Card>
        </Grid>

      </Grid>

      <Backdrop
        className={classes.backdrop}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

Views.propTypes = {
  className: PropTypes.string,
};

export default Views;
